import { SHAPE_PREFIX } from "@/store/useCanvasStore";

// Shared touch-selection helpers for the mobile canvas. Kept framework-free so
// they can be unit-tested without mounting ReactFlow.

export interface CanvasTarget {
  type: "node" | "edge" | "shape";
  /** ctx-menu style id — shapes have SHAPE_PREFIX stripped. */
  id: string;
  /** full ReactFlow id — shapes keep SHAPE_PREFIX (matches selectedIds). */
  rfId: string;
}

/**
 * Walk up from a DOM element to the nearest ReactFlow node/edge and resolve it
 * to a canvas target. Returns null for the pane or unrelated elements.
 */
export function resolveCanvasTarget(el: Element | null): CanvasTarget | null {
  if (!el) return null;

  const nodeEl = el.closest(".react-flow__node");
  if (nodeEl) {
    const rfId = nodeEl.getAttribute("data-id") ?? "";
    if (!rfId) return null;
    const isShape = rfId.startsWith(SHAPE_PREFIX);
    return {
      type: isShape ? "shape" : "node",
      id: isShape ? rfId.slice(SHAPE_PREFIX.length) : rfId,
      rfId,
    };
  }

  const edgeEl = el.closest(".react-flow__edge");
  if (edgeEl) {
    const rfId = edgeEl.getAttribute("data-id") ?? "";
    if (!rfId) return null;
    return { type: "edge", id: rfId, rfId };
  }

  return null;
}

/** Return a new Set with `id` toggled — never mutates the input. */
export function toggleInSet(ids: Set<string>, id: string): Set<string> {
  const next = new Set(ids);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  return next;
}

/**
 * Drop ReactFlow's built-in `select` changes. On mobile we drive selection
 * ourselves (additive tap), so RF's default single-select must not overwrite it.
 */
export function stripSelectChanges<T extends { type: string }>(changes: T[]): T[] {
  return changes.filter((c) => c.type !== "select");
}
